import { LitElement, html, nothing } from "lit";

import { customElement, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";

import { LayoutController } from "ui-common";
import "ui-components/button/wow-button";
import "ui-components/dropdown-menu/dropdown-menu";
import "ui-components/dropdown-menu/dropdown-menu-item";
import "ui-components/skeleton-rect";
import "ui-components/feedback/create-feedback-modal";
import "../../components/content-container/content-container";
import { scrollableColumnStyles } from "../../components/content-container/content-container.css";
import "../../components/create-link-modal/new-create-link-modal";
import "../../components/link-card";
import "../../components/page-content/page-content";
import { globalStyles } from "../../components/worlds-layout/worlds-layout-global.css";

import { styles } from "./links-page.css";

import type { UnifiedLink } from "api-contracts/rpc/get-unified-links";
import { appContext } from "ui-common/appContext";
import { DataQueryController } from "ui-common/data-query-controller";
import { NotificationService } from "ui-components/notification/notification-service";
import type { PreviewProfile } from "../../components/preview-profile";
import type { DraggedItem } from "../../controllers/dnd-container-controller";
import { DnDContainerController } from "../../controllers/dnd-container-controller";
import { DnDDropIndicatorController } from "../../controllers/dnd-drop-indicator-controller";
import { unifiedLinksQueryOptions } from "../../queries/unified-links";
import { updateUnifiedLinksOrderMutation } from "../../services/unified-link-mutation";
import { t, translate } from "../../translations/i18n";

export interface LinkItem {
  id: string;
  title: string;
  url: string;
  imageUrl: string | null;
}

type LinkFilter = "live" | "upcoming" | "expired";

// Create a type alias that extends DraggedItem
type LinkItemWithId = LinkItem & DraggedItem;

@customElement("links-page")
export class LinksPage extends LitElement {
  static override styles = [styles, globalStyles, scrollableColumnStyles];

  private unifiedLinksQuery = new DataQueryController(this, () => unifiedLinksQueryOptions);

  private layoutController = new LayoutController(this);
  private previewProfileRef = createRef<PreviewProfile>();

  // Create refs for the new controller architecture
  private containerRef = createRef<HTMLElement>();

  private dndController = new DnDContainerController<LinkItemWithId>(this, this.containerRef, {
    onReorder: async (newOrder: LinkItemWithId[]) => {
      const linkIds = newOrder.map((link) => link.id);

      try {
        await updateUnifiedLinksOrderMutation.mutate({ linkIds });
        this.handleLinkSaved();
      } catch (error) {
        console.error("Failed to update links order:", error);

        // Show error notification
        NotificationService.show({
          message: translate("reorderLinks.reorderError"),
          severity: "error",
        });
      }
    },
    onDropComplete: (droppedItem: LinkItemWithId) => {
      // Trigger flourish effect for the specific dropped card
      this.flourishCardId = droppedItem.id;
      setTimeout(() => {
        this.flourishCardId = null;
      }, 600);
    },
    getValidDropPositions: (sourceItem: LinkItemWithId) => {
      // Links-specific logic: rectangles use top/bottom, squares use left/right
      const sourceHasImage = Boolean(sourceItem.imageUrl);
      return sourceHasImage ? ["left", "right"] : ["top", "bottom"];
    },
  });

  private dropIndicatorController = new DnDDropIndicatorController(this, this.containerRef);

  @state()
  private showCreateModal = false;

  @state()
  private showFeedbackModal = false;

  @state()
  private flourishCardId: string | null = null;

  @state()
  private selectedFilter: LinkFilter = "live";

  override connectedCallback() {
    super.connectedCallback();

    const url = new URL(window.location.href);
    const params = url.searchParams;
    const value = params.get("create");

    // Convert to boolean (supports ?create, ?create=true, ?create=false)
    const shouldCreate = value !== null && (value === "" || value.toLowerCase() === "true");

    if (shouldCreate) {
      this.showCreateModal = true;
      // Remove the param from the URL (no reload)
      params.delete("create");
      window.history.replaceState({}, "", url);
    }
  }

  private handleLinkSaved = () => {
    this.previewProfileRef.value?.reloadIframe();
    this.unifiedLinksQuery.refetch();
  };

  private handleLinkCreated = () => {
    this.handleLinkSaved();

    if (this.unifiedLinksQuery.result.data?.links?.length === 1) {
      this.showFeedbackModal = true;
    }
  };

  private handleModalClose = () => {
    this.showCreateModal = false;
  };

  private handleOpenCreateModal = () => {
    this.showCreateModal = true;
  };

  private handleOpenProfile = () => {
    const handle = appContext.currentUser?.handle;

    if (handle) window.open(`/${handle}`, "_self", "noopener,noreferrer");
  };

  private handleDnDDropRequested = () => {
    // Handle drop request from the container controller
    const { data: linksData } = this.unifiedLinksQuery.result;
    if (linksData?.links) {
      // CRITICAL: Use filtered links that match what's displayed on screen
      // This ensures drag positions map correctly to visual positions
      const filteredLinks = this.getFilteredLinks(linksData.links, this.selectedFilter);
      const links = filteredLinks.map((ld) => ({
        ...ld,
        imageUrl: ld.promotion_file?.path ?? null,
      }));
      this.dndController.processDropWithItems(links);
    }
  };

  private handleFilterChange = (filter: LinkFilter) => {
    this.selectedFilter = filter;
  };

  private renderLinksContent(links: UnifiedLink[], isPending: boolean, error: Error | null) {
    if (isPending) {
      return html`
        <div class="links-grid">
          <skeleton-rect class="skeleton-rectangle" width="100%" height="86px"></skeleton-rect>
          <skeleton-rect class="skeleton-rectangle" width="100%" height="86px"></skeleton-rect>
          <skeleton-rect class="skeleton-square" width="100%" height="100%"></skeleton-rect>
          <skeleton-rect class="skeleton-square" width="100%" height="100%"></skeleton-rect>
          <skeleton-rect class="skeleton-rectangle" width="100%" height="86px"></skeleton-rect>
        </div>
      `;
    }

    if (error) {
      return html`<div class="error">${t("links.errorLoading")} ${error.message}</div>`;
    }

    if (!links.length) {
      return html`
        <div class="empty-state">
          <h2 class="empty-state-title">${t("links.emptyStateTitle")}</h2>
          <wow-button variant="neutral" size="small" @click=${this.handleOpenCreateModal}>
            ${t("links.addLinks")}
          </wow-button>
        </div>
      `;
    }

    return html`
      <div
        ${ref(this.containerRef)}
        class="links-grid ${this.dndController.isDragging ? "drag-active" : ""}"
        style="position: relative;"
        @dnd-drop-requested=${this.handleDnDDropRequested}
      >
        ${repeat(
          links,
          (link) => link.id,
          (link) =>
            html`<link-card
              .link=${link}
              .flourish=${this.flourishCardId === link.id}
              @link-deleted=${this.handleLinkSaved}
              @link-created=${this.handleLinkSaved}
              @link-edited=${this.handleLinkSaved}
            ></link-card>`,
        )}
        ${this.dropIndicatorController.renderDropIndicator(
          this.dndController.currentDropPosition,
          this.dndController.currentDropPosition ? this.dndController.getValidDropPositions() : [],
          this.dndController.isDragging,
        )}
      </div>
    `;
  }

  getFilteredLinks(links: UnifiedLink[], filter: LinkFilter): UnifiedLink[] {
    switch (filter) {
      case "live":
        return links.filter((l) => l.status === "live");
      case "upcoming":
        return links.filter((l) => l.status === "upcoming");
      case "expired":
        return links.filter((l) => l.status === "expired");
      default:
        return links;
    }
  }

  protected override render() {
    const { data: linksData, isPending, error } = this.unifiedLinksQuery.result;
    const isEmpty = !isPending && !error && !linksData?.links?.length;

    const links = linksData?.links || [];
    const counts = linksData?.counts || { live: 0, upcoming: 0, expired: 0, total: 0 };
    const filteredLinks = this.getFilteredLinks(links, this.selectedFilter);

    return html`
      <page-content .pageTitle=${t("pages.links")}>
        <div slot="actions">
          <view-primary-buttons
            @primary-click=${this.handleOpenCreateModal}
            @view-click=${this.handleOpenProfile}
          ></view-primary-buttons>
        </div>
        <div class="containers">
          <content-container variant="primary">
            <div
              slot="column"
              class=${classMap({
                "links-content": true,
                "links-content-empty": isEmpty,
              })}
            >
              ${links.length
                ? html`
                    <div class="links-filter">
                      <wow-button
                        size="small"
                        variant=${this.selectedFilter === "live" ? "primary" : "subtle"}
                        @click=${() => this.handleFilterChange("live")}
                        ?disabled=${counts.live === 0}
                      >
                        ${t("links.filters.live")} (${counts.live})
                      </wow-button>
                      <wow-button
                        size="small"
                        variant=${this.selectedFilter === "upcoming" ? "primary" : "subtle"}
                        @click=${() => this.handleFilterChange("upcoming")}
                        ?disabled=${counts.upcoming === 0}
                      >
                        ${t("links.filters.upcoming")} (${counts.upcoming})
                      </wow-button>
                      ${counts.expired
                        ? html`
                            <wow-button
                              size="small"
                              variant=${this.selectedFilter === "expired" ? "primary" : "subtle"}
                              @click=${() => this.handleFilterChange("expired")}
                              ?disabled=${counts.expired === 0}
                            >
                              ${t("links.filters.expired")} (${counts.expired})
                            </wow-button>
                          `
                        : nothing}
                    </div>
                  `
                : ""}
              <div class="scrollable-column-content">${this.renderLinksContent(filteredLinks, isPending, error)}</div>
            </div>
          </content-container>

          <!-- Secondary Area (hidden on mobile) -->
          ${this.layoutController.isDesktop
            ? html`
                <content-container variant="secondary" columns="1">
                  <div slot="column">
                    <preview-profile page="/" ${ref(this.previewProfileRef)}></preview-profile>
                  </div>
                </content-container>
              `
            : nothing}
        </div>
      </page-content>

      ${this.showCreateModal
        ? html`<new-create-link-modal
            @modal-close=${this.handleModalClose}
            @link-created=${this.handleLinkCreated}
            @link-edited=${this.handleLinkSaved}
          ></new-create-link-modal>`
        : nothing}
      ${this.showFeedbackModal
        ? html`<create-feedback-modal
            headerText=${t("feedback.headerText")}
            sentimentMessage=${t("feedback.sentimentMessage", {
              context: this.unifiedLinksQuery.result.data?.links[0]?.has_discount ? "discount" : "link",
            })}
            sentimentPrompt=${t("feedback.sentimentPrompt")}
            labelPrompt=${t("feedback.labelPrompt")}
            labelPlaceholder=${t("feedback.labelPlaceholder")}
            labelError=${t("feedback.labelError")}
            successMessage=${t("feedback.successMessage")}
            footerButtonText=${t("feedback.footerButtonText")}
            footerButtonLoadingText=${t("feedback.footerButtonLoadingText")}
            @close=${() => (this.showFeedbackModal = false)}
          ></create-feedback-modal>`
        : nothing}
    `;
  }
}
